import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "../../utils/authContext";
import api, { baseURL } from "../../utils/api";

export const LOGIN_GATE_AT = 3;

const COOKIE_CONSENT_ANON_USER_KEY = "thesea_cookie_consent_anon";
const COOKIE_CONSENT_AUTH_USER_KEY = "thesea_cookie_consent_authed";

function readCookieChoice(isLoggedIn) {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(isLoggedIn ? COOKIE_CONSENT_AUTH_USER_KEY : COOKIE_CONSENT_ANON_USER_KEY);
}

const TheSeaContext = createContext(null);

export function TheSeaProvider({ children }) {
  // The Sea shares mehire's real session store (authContext.js) instead of keeping its own
  // copy — authedUser is always the backend's login/signup response body, so userName and
  // login state here can never drift out of sync with the rest of the app.
  const { authedUser, login, logout } = useAuth();
  const isLoggedIn = !!authedUser;
  const accountName = authedUser?.userName || "";
  const accountEmail = authedUser?.email || "";

  const [questionCount, setQuestionCount] = useState(0);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [conversationLogs, setConversationLogs] = useState([]); // most recent first, capped at 10

  // Cookie consent is tracked separately for anonymous vs. logged-in visitors, so a
  // first-time login always surfaces the banner again even if already dismissed anonymously.
  // Only "accepted" is ever persisted — a decline is never written to storage, so the
  // banner returns on the next visit; it's just dismissed (not accepted) for this session.
  const [cookieChoice, setCookieChoiceState] = useState(() => readCookieChoice(isLoggedIn));
  const [cookieDismissed, setCookieDismissed] = useState(false);

  useEffect(() => {
    setCookieChoiceState(readCookieChoice(isLoggedIn));
    setCookieDismissed(false);
  }, [isLoggedIn]);

  // The server is the source of truth for the point balance; re-sync it whenever the login
  // state flips (covers initial load, signup, login, and logout all in one place).
  useEffect(() => {
    if (!isLoggedIn) {
      setPointsBalance(0);
      return;
    }

    let cancelled = false;
    api.get(`${baseURL}/authenticated/point/balance`)
      .then(({ data }) => {
        if (!cancelled) setPointsBalance(data?.balance ?? 0);
      })
      .catch((err) => console.error("Failed to fetch point balance:", err));

    return () => { cancelled = true; };
  }, [isLoggedIn]);

  // Mirror of the point-balance fetch above, but for anonymous visitors: read the backend's
  // persisted question count on load instead of trusting a client-side counter that resets to
  // 0 on every page refresh.
  useEffect(() => {
    if (isLoggedIn) {
      setQuestionCount(0);
      return;
    }

    let cancelled = false;
    api.get(`${baseURL}/anonymous/copilot/usage`)
      .then(({ data }) => {
        if (!cancelled) setQuestionCount(data?.questionCount ?? 0);
      })
      .catch((err) => console.error("Failed to fetch copilot usage:", err));

    return () => { cancelled = true; };
  }, [isLoggedIn]);

  const setCookieChoice = useCallback((choice) => {
    if (choice === "accepted") {
      window.localStorage.setItem(isLoggedIn ? COOKIE_CONSENT_AUTH_USER_KEY : COOKIE_CONSENT_ANON_USER_KEY, choice);
      setCookieChoiceState(choice);
    } else {
      setCookieDismissed(true);
    }
  }, [isLoggedIn]);

  const logConversation = useCallback((question) => {
    setConversationLogs((prev) => [{ question, time: new Date() }, ...prev].slice(0, 10));
  }, []);

  // --- Chat state (bug fix: "chat resets when navigating away from Home and back") ---
  //
  // This used to be plain `useState` inside ChatAgent.js itself. The problem: ChatAgent is
  // only rendered by TheSeaHome (see TheSeaHome.js), and TheSeaHome is a *route* component —
  // React Router unmounts it whenever you leave "/" and mounts a fresh copy when you come
  // back. Unmounting a component throws away all of its `useState`, so every trip away from
  // Home and back wiped the conversation to empty, no matter what the backend had stored.
  //
  // The fix is to move the state up to here, in TheSeaContext. Look at App.js: the
  // <TheSeaProvider> that creates this context wraps the *parent* route
  // (`<Route path="/" element={<TheSeaProvider><TheSeaLayout/></TheSeaProvider>}>`), and Home,
  // About, Blog, etc. are all *children* of that same route, rendered inside TheSeaLayout's
  // <Outlet/>. Only the <Outlet/> content swaps when you navigate between those children —
  // TheSeaProvider (and everything inside it, including this state) stays mounted the whole
  // time you're anywhere under "/". So as long as ChatAgent reads/writes its messages through
  // this context instead of its own local state, the messages survive Home unmounting.
  //
  // Nothing here talks to the backend/DynamoDB history table — this only fixes state loss
  // *within a single browser session* (tab open, navigating around). Closing the tab or
  // refreshing the page still clears it, because none of this is persisted to storage.
  const [chatMessages, setChatMessages] = useState([]);       // the list of chat bubbles (user + agent)
  const [chatIsTyping, setChatIsTyping] = useState(false);     // shows the "..." typing indicator
  const [chatLocked, setChatLocked] = useState(false);         // true once a login/paywall gate is shown, disables input
  const [chatLockedPlaceholder, setChatLockedPlaceholder] = useState(""); // input placeholder text while locked
  const [chatInputValue, setChatInputValue] = useState("");    // current text in the chat input box
  const [chatSelectedChips, setChatSelectedChips] = useState([]); // which suggestion chips (ICH, FDA, ...) are toggled on
  const chatNextId = useRef(1); // counter used to give each new message a unique React key

  // Appends one message ({role, text, ...}) to chatMessages, tagging it with a fresh id.
  // Used for both the user's own question and every agent/gate reply.
  const addChatMessage = useCallback((msg) => {
    chatNextId.current += 1;
    const id = "m" + chatNextId.current;
    setChatMessages((prev) => [...prev, { id, ...msg }]);
    return id;
  }, []);

  // Clears the conversation back to a blank slate. Called when the user clicks "New chat".
  const resetChat = useCallback(() => {
    setChatMessages([]);
    setChatIsTyping(false);
    setChatLocked(false);
    setChatLockedPlaceholder("");
    setChatInputValue("");
    setChatSelectedChips([]);
  }, []);

  const value = {
    questionCount, setQuestionCount,
    isLoggedIn, login, logout,
    pointsBalance, setPointsBalance,
    accountEmail,
    accountName,
    conversationLogs, logConversation,
    cookieChoice, setCookieChoice,
    cookiesAccepted: cookieChoice === "accepted",
    showCookieBanner: cookieChoice !== "accepted" && !cookieDismissed,
    // Exposed so ChatAgent.js can read/update the chat without owning any of the state itself.
    chatMessages, addChatMessage,
    chatIsTyping, setChatIsTyping,
    chatLocked, setChatLocked,
    chatLockedPlaceholder, setChatLockedPlaceholder,
    chatInputValue, setChatInputValue,
    chatSelectedChips, setChatSelectedChips,
    resetChat,
  };

  return <TheSeaContext.Provider value={value}>{children}</TheSeaContext.Provider>;
}

export function useTheSeaContext() {
  const ctx = useContext(TheSeaContext);
  if (!ctx) throw new Error("useTheSeaContext must be used within TheSeaProvider");
  return ctx;
}
